import React, { useState } from 'react';
import { Timer, Clock, Bell, Plus, X, Trash2 } from 'lucide-react';
import type { Timer as TimerType, Alarm, AlarmRepeat } from '@/types/clock';

interface TimerAlarmSettingsProps {
  timers: TimerType[];
  alarms: Alarm[];
  onAddTimer: (durationMs: number, label?: string) => void;
  onCancelTimer: (id: string) => void;
  onAddAlarm: (time: { hour: number; minute: number }, repeat: AlarmRepeat, label?: string) => void;
  onToggleAlarm: (id: string, enabled: boolean) => void;
  onDeleteAlarm: (id: string) => void;
  accentColor: string;
  initialTab?: 'timer' | 'alarm';
}

const TIMER_PRESETS = [
  { label: '1 min', ms: 60000 },
  { label: '5 min', ms: 300000 },
  { label: '10 min', ms: 600000 },
  { label: '15 min', ms: 900000 },
  { label: '30 min', ms: 1800000 },
  { label: '1 hour', ms: 3600000 },
];

export const TimerAlarmSettings: React.FC<TimerAlarmSettingsProps> = ({
  timers,
  alarms,
  onAddTimer,
  onCancelTimer,
  onAddAlarm,
  onToggleAlarm,
  onDeleteAlarm,
  accentColor,
  initialTab = 'timer'
}) => {
  const [activeTab, setActiveTab] = useState<'timer' | 'alarm'>(initialTab);
  const [customMinutes, setCustomMinutes] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  
  // Alarm form state
  const [alarmHour, setAlarmHour] = useState('12');
  const [alarmMinute, setAlarmMinute] = useState('00');
  const [alarmRepeat, setAlarmRepeat] = useState<AlarmRepeat>('once');
  const [alarmLabel, setAlarmLabel] = useState('');

  const handleAddCustomTimer = () => {
    const mins = parseInt(customMinutes);
    if (!mins || mins <= 0) return;
    
    onAddTimer(mins * 60000, customLabel || undefined);
    // Don't clear the form - let user create multiple timers easily
    // User can manually clear if they want different values
  };

  const handleAddAlarm = () => {
    const hour = parseInt(alarmHour);
    const minute = parseInt(alarmMinute);
    
    if (isNaN(hour) || isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      return;
    }
    
    onAddAlarm({ hour, minute }, alarmRepeat, alarmLabel || undefined);
    setAlarmLabel('');
  };

  const formatAlarmTime = (time: { hour: number; minute: number }) => {
    const period = time.hour >= 12 ? 'PM' : 'AM';
    const hour = time.hour % 12 || 12;
    const minute = time.minute.toString().padStart(2, '0');
    return `${hour}:${minute} ${period}`;
  };

  const getNextTriggerText = (nextTriggerTs: number) => {
    const now = Date.now();
    const diff = nextTriggerTs - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `in ${days}d`;
    }
    if (hours > 0) {
      return `in ${hours}h ${minutes}m`;
    }
    return `in ${minutes}m`;
  };

  return (
    <div className="space-y-4">
      {/* Tab Selector */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('timer')}
          className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
            activeTab === 'timer'
              ? 'bg-blue-500/30 text-white border-2 border-blue-400/50'
              : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
          }`}
        >
          <Timer className="w-4 h-4" />
          Timer
        </button>
        <button
          onClick={() => setActiveTab('alarm')}
          className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
            activeTab === 'alarm'
              ? 'bg-blue-500/30 text-white border-2 border-blue-400/50'
              : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
          }`}
        >
          <Bell className="w-4 h-4" />
          Alarm
        </button>
      </div>

      {/* Timer Tab */}
      {activeTab === 'timer' && (
        <div className="space-y-4">
          {/* Quick Presets */}
          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">Quick Start</label>
            <div className="grid grid-cols-3 gap-2">
              {TIMER_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => onAddTimer(preset.ms)}
                  className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 text-sm font-medium transition-all hover:scale-105"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Timer */}
          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">Custom Timer</label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="number"
                  value={customMinutes}
                  onChange={(e) => setCustomMinutes(e.target.value)}
                  placeholder="Minutes"
                  min="1"
                  className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-blue-400/50"
                />
                <button
                  onClick={handleAddCustomTimer}
                  disabled={!customMinutes || parseInt(customMinutes) <= 0}
                  className="px-4 py-2 rounded-lg bg-blue-500/30 hover:bg-blue-500/40 border border-blue-400/50 text-white font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Start
                </button>
              </div>
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="Label (optional)"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-blue-400/50"
              />
            </div>
          </div>

          {/* Active Timers */}
          {timers.length > 0 && (
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Active Timers</label>
              <div className="space-y-2">
                {timers.map((timer) => (
                  <div
                    key={timer.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                  >
                    <div>
                      <div className="text-white font-medium">{timer.label || 'Timer'}</div>
                      <div className="text-white/50 text-xs">{Math.ceil(timer.durationMs / 60000)} minutes</div>
                    </div>
                    <button
                      onClick={() => onCancelTimer(timer.id)}
                      className="p-2 rounded-lg hover:bg-red-500/20 transition-colors text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Alarm Tab */}
      {activeTab === 'alarm' && (
        <div className="space-y-4">
          {/* Add Alarm Form */}
          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">Set Alarm</label>
            <div className="space-y-3">
              {/* Time Picker */}
              <div className="flex gap-2">
                <input
                  type="number"
                  value={alarmHour}
                  onChange={(e) => setAlarmHour(e.target.value)}
                  placeholder="HH"
                  min="0"
                  max="23"
                  className="w-20 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-center placeholder-white/40 focus:outline-none focus:border-blue-400/50"
                />
                <span className="text-white/60 text-xl flex items-center">:</span>
                <input
                  type="number"
                  value={alarmMinute}
                  onChange={(e) => setAlarmMinute(e.target.value)}
                  placeholder="MM"
                  min="0"
                  max="59"
                  className="w-20 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-center placeholder-white/40 focus:outline-none focus:border-blue-400/50"
                />
              </div>

              {/* Repeat */}
              <div className="grid grid-cols-2 gap-2">
                {(['once', 'daily', 'weekdays', 'weekends'] as AlarmRepeat[]).map((repeat) => (
                  <button
                    key={repeat}
                    onClick={() => setAlarmRepeat(repeat)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      alarmRepeat === repeat
                        ? 'bg-blue-500/30 text-white border border-blue-400/50'
                        : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    {repeat.charAt(0).toUpperCase() + repeat.slice(1)}
                  </button>
                ))}
              </div>

              {/* Label */}
              <input
                type="text"
                value={alarmLabel}
                onChange={(e) => setAlarmLabel(e.target.value)}
                placeholder="Label (optional)"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-blue-400/50"
              />

              {/* Add Button */}
              <button
                onClick={handleAddAlarm}
                className="w-full px-4 py-3 rounded-lg bg-blue-500/30 hover:bg-blue-500/40 border border-blue-400/50 text-white font-semibold transition-all hover:scale-105 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Alarm
              </button>
            </div>
          </div>

          {/* Alarm List */}
          {alarms.length > 0 && (
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Your Alarms</label>
              <div className="space-y-2">
                {alarms.map((alarm) => (
                  <div
                    key={alarm.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-bold text-lg">{formatAlarmTime(alarm.time)}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/60">
                          {alarm.repeat}
                        </span>
                      </div>
                      {alarm.label && (
                        <div className="text-white/60 text-sm mt-1">{alarm.label}</div>
                      )}
                      {alarm.enabled && (
                        <div className="text-xs text-blue-400 mt-1">{getNextTriggerText(alarm.nextTriggerTs)}</div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Toggle */}
                      <button
                        onClick={() => onToggleAlarm(alarm.id, !alarm.enabled)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          alarm.enabled ? 'bg-blue-500/50' : 'bg-white/10'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                            alarm.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      
                      {/* Delete */}
                      <button
                        onClick={() => onDeleteAlarm(alarm.id)}
                        className="p-2 rounded-lg hover:bg-red-500/20 transition-colors text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
